import Opaque from 'ts-opaque'
import { FullConfig, PortConfig, PortNumber, PortType, PortValueName, PortValues } from './ports'
import { ElectricCar, ViewWeek, AcUnit, Water, Landscape, HotTub, Flood, PropaneTank, CalendarMonth, Euro, Handyman, HdrAuto, PanTool, WbSunny, ChargingStation } from '@mui/icons-material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { SvgIconTypeMap } from '@mui/material'
import { ApplianceData } from '../classes/appliances/Appliance'

/**
 * Any ID.
 */
export type ID = Opaque<number, 'ID'>
/**
 * A string 'YYYY-MM-DD hh:mm:ss'.
 */
export type TimeString = Opaque<string, 'TimeString'>
/**
 * A string 'hh:mm'.
 */
export type TimeHHMM = Opaque<string, 'TimeHHMM'>
/**
 * A code to recognize a single physical device.
 */
export type DeviceHwID = Opaque<string, 'DeviceHwID'>
/**
 * Internet address types.
 */
export type IPV4AddressAndPort = Opaque<string, 'IPV4AddressAndPort'>
export type IPV6AddressAndPort = Opaque<string, 'IPV6AddressAndPort'>
export type Hostname = Opaque<string, 'Hostname'>
/**
 * Zip code
 */
export type ZipCode = Opaque<string, 'ZipCode'>

/**
 * Supported billing area codes.
 */
export type AreaCode = 'SYS' | 'SE1' | 'SE2' | 'SE3' | 'SE4' | 'FI' | 'DK1' | 'DK2' | 'OSLO' | 'KR.SAND' | 'BERGEN' | 'MOLDE' | 'TR.HEIM' | 'TROMSØ' | 'EE' | 'LV' | 'LT' | 'AT' | 'BE' | 'DE-LU' | 'FR' | 'NL'

/**
 * Source for market prices.
 */
export type PriceSource = 'NORDPOOL' | 'ENTSOE'

/**
 * Supported languages.
 */
export type Language = 'en' | 'fi'

/**
 * Supported currencies.
 */
export type Currency = 'EUR'

/**
 * User access level description.
 */
export type Role = 'READER' | 'WRITER' | 'ADMIN' | 'ROOT'

/**
 * Features provided by subscription.
 */
export type Feature =
  'APPLIANCE_PAGE' |
  'CI_CONTROL' |
  'DEBUG_PAGE' |
  'DEVICE_OPTIMIZER' |
  'HISTORY_PAGE' |
  'LOGIN' |
  'MY_ACTIVITY' |
  'OPTIMIZER_CONTROL' |
  'PRICE_OPTIMIZATION' |
  'PRO_CONFIG_PAGE' |
  'WIDGET_PAGE' |
  'FCR_BATTERIES' |
  'HEAT_PUMP_APPLIANCE' |
  'HEAT_PUMP_HISTORY'

/**
 * Billing area.
 */
export interface Area {
  id?: ID
  name: string
  code: AreaCode
}

export interface Package {
  id?: ID
  area: Area
  code: string
  features: Feature[]
}

/**
 * A subscription to the package.
 */
export interface Subscription {
  id?: ID
  expired: boolean
  expires: TimeString
  package: Package
}

/**
 * Multi-optimization configuration mode
 */
export type MultioptConfigMode =
    'MULTIOPT_CONFIG_AUTO' |
    'MULTIOPT_CONFIG_LEADER' |
    'MULTIOPT_CONFIG_FOLLOWER' |
    'MULTIOPT_CONFIG_NOOPT'

/**
 * User settings.
 */
export type ElectricityContractType = 'FIXED' | 'MARKET' | 'MARKET_HOURLY' | 'MARKET_CONSUMPTION_IMPACT'

/**
 * Contract party of the reserve market settlement.
 */
export type ReserveParty = 'PERSON' | 'COMPANY'

// How the balance responsible party for reserve-use notifications is resolved:
// via a Fingrid Datahub authorization, or from the user's own retailer choice.
export type BrpMethod = 'datahub' | 'manual'

/**
 * How the VAT liability of the reserve market settlement was resolved.
 */
export type VatSource = 'PRH' | 'USER'

/**
 * Company details looked up from the open YTJ API of PRH.
 */
export interface CompanyInfo {
  businessId: string
  name: string | null
  address: string | null
  active: boolean
  situations: string[]
  vatLiable: boolean
  vatDescription: string | null
  vatRegisteredAt: string | null
}
export interface UserSettings {
  cheapStartTime?: TimeHHMM | null
  cheapEndTime?: TimeHHMM | null
  cheapTransferPrice?: number | null
  contractType?: ElectricityContractType | ''
  electricityPrice?: number | null
  hasCheapTransfer?: boolean
  marginPrice?: number | null
  transferPrice?: number | null
  meterNumber?: string
  retailerCode?: string | null
  brpMethod?: BrpMethod | null
  zipCode?: ZipCode
  hasReserveMarkets?: boolean
  hasAcceptReserveMarkets?: boolean
  contractName?: string
  contractAddress?: string
  iban?: string
  reserveParty?: ReserveParty
  reserveBusinessId?: string
  isVatRegistered?: boolean
  reserveVatSource?: VatSource
  reserveVatCheckedAt?: string
  /** Whether the History page's money figures include margin and transfer fee. */
  historyIncludeFees?: boolean
}

/**
 * Logged in user data.
 */
export interface FcrDeviceStatus {
  hw_id: string
  commissioning_state: number | null
  commissioning_errcode: number | null
  // Inverter nominal power (W) from the FCR appliance config; the calibration
  // dialog needs it to pick the port-310 power unit (1 W vs 16 W).
  nominal_power: number | null
}

export interface DiagnosticsSummary {
  warnings: number
  errors: number
  has_comments?: boolean
}

export interface DiagnosticEntry {
  id: number
  device_id: number
  hw_id: string | null
  check_type: string
  severity: string
  status: string
  message: string
  details: Record<string, unknown>
  detected_at: string
  resolved_at: string | null
  acknowledged_by: number | null
  acknowledged_at: string | null
  bypass_reason: string | null
}

export interface DeviceCommentEntry {
  id: number
  device_id: number
  user_email: string | null
  body: string
  inserted_at: string
}

export interface DiagnosticsData {
  diagnostics: DiagnosticEntry[]
  comments: DeviceCommentEntry[]
  settings: {
    diagnostic_emails_installer: boolean
    diagnostic_emails_customer: boolean
  }
  blocking_groups: {
    installer: string[]
    customer: string[]
  }
}

export interface User {
  id?: ID
  name: string
  email: string
  phone: string | null
  country?: Country
  area?: Area
  language: Language
  currency: Currency
  role: Role
  features: Feature[]
  subscriptions?: Subscription[]
  settings: UserSettings
  last_ip: string
  last_login: TimeString
  force_new_password: string | null
  is_verified?: boolean
  announcements?: Announcement[]
  groups?: string[]
  fcr_devices?: FcrDeviceStatus[]
  diagnostics?: DiagnosticsSummary | null
}

/**
 * User groups.
 */
export interface Group {
  id?: ID
  name: string
  children?: Group[]
  permanent?: boolean,
  parent_group_id?: ID,
  editable?: boolean,
  // Only for group admins: members that block deleting the group, direct sub-organisations and
  // users' permanent default groups under the group.
  user_count?: number,
  child_count?: number,
  default_group_count?: number,
  settings?: {
    diagnostic_emails_installer?: boolean
    diagnostic_emails_customer?: boolean
    [key: string]: unknown
  }
}

/**
 * A group the user is a member of, together with the user's role in it.
 */
export interface GroupMembership {
  id: ID
  name: string
  role: Role
  permanent: boolean
  parent_group_id: ID | null
}

/**
 * An account that holds a role on a user's personal group, and therefore sees that user's
 * site/dashboard directly without impersonation.
 */
export interface SiteAccessAccount {
  id: ID
  email: string
  role: Role
}

/**
 * A country definition.
 */
export interface Country {
  id?: ID
  name: string
  iso_3166: string
}

/**
 * Description of physical location of device.
 */
export interface Location {
  id?: ID
  name: string
  iso_3166: string
  address: string
  grid_connection_id: string
  latitude: number
  longitude: number
  postal_code: string
  multiopt_server_proxy?: boolean
  user: User
  country: Country
  appliances: ApplianceData[]
}

/**
 * Description of a port for UI.
 */
export interface DevicePortSpec {
  port_number: PortNumber
  type: PortType
  voltage?: number
  current?: number
  slot: null
}

/**
 * Parameters for device specification.
 */
export interface DeviceSpecParams {
  ports: DevicePortSpec[]
}

/**
 * Device specification.
 */
export interface DeviceSpec {
  id?: ID
  manufacturer: string
  model: string
  description: string
  params: DeviceSpecParams
}

export interface FCR {
  enabled: boolean
  inverter: string
  battery: string
  capacity: number
  power: number
  solar_connected: boolean
  solar_connected_power: number
  solar_onprem: boolean
  solar_onprem_power: number
}

export type DeviceAlertMode = 'email' | 'slack' | 'none'
/**
 * Additional device variables.
 */
export interface DeviceValues {
  alert?: DeviceAlertMode
  alert_token?: string
  last_offline_alert?: TimeString
  slave_of?: DeviceHwID | null
  master_of?: DeviceHwID[]
  online?: boolean
  disable_upgrade?: boolean
  firmware_version?: string
  last_data?: TimeString
  multiopt_config_mode?: MultioptConfigMode
  /** LAN IPv4 address reported by the device on port F00 (null = none/unknown). */
  lan_ip?: string | null
  lan_ip_at?: TimeString
}

/**
 * A device.
 */
export interface Device {
  id?: ID
  user: User | null
  name: string
  hw_id: DeviceHwID
  location_id?: ID
  location: Location | null
  device_spec: DeviceSpec | null
  device_spec_id?: ID
  comments: string
  ports?: Port[]
  rules?: Rule[]
  values?: DeviceValues
  owner?: User
}

/**
 * Visual type for connected appliance.
 */
export type RelayType = 'RADIATOR' | 'AIR-AIR-HEAT-PUMP' | 'AIR-WATER-HEAT-PUMP' | 'GROUND-SOURCE-HEAT-PUMP' | 'EV-CHARGER' | 'BOILER' | 'IN-FLOOR-HEATING' | 'STORAGE-TANK'
export const applianceTypes: RelayType[] = [
  'EV-CHARGER',
  'RADIATOR',
  'AIR-AIR-HEAT-PUMP',
  'AIR-WATER-HEAT-PUMP',
  'GROUND-SOURCE-HEAT-PUMP',
  'BOILER',
  'IN-FLOOR-HEATING',
  'STORAGE-TANK'
]
export const applianceIcons: (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
  muiName: string;
})[] = [
  ElectricCar,
  ViewWeek,
  AcUnit,
  Water,
  Landscape,
  HotTub,
  Flood,
  PropaneTank,
]

/**
 * A description for flash data.
 */
export type PortFlashInfo = {
  port: PortNumber
  config: PortConfig
}

/**
 * Possible error flags for a port.
 */
export type PortFlag = 'TIMEOUT' | // Port hasn't sent anything lately.
  'PACKET_LOSS' | // Seq number's missing recently.
  'NO_RESPONSE' | // Does not seem to react commands correctly.
  'RC_NON_ZERO' | // The port reports error code.
  'INCOMPATIBLE' | // Cannot parse payload received.
  'COMMUNICATIONS' | // Communication failure to gadget.
  'COMMUNICATIONS_PUBLIC_INTERNET' // Enion has a public IP (no router); firmware refuses to look for the inverter.

/**
 * Color of the port indicator light.
 */
export type PortStatus = 'unknown' | 'none' | 'ok' | 'success' | 'error' | 'warning'

/**
 * Port data.
 */
export interface Port {
  id?: ID
  device_id?: ID
  rule_id?: ID
  type: PortType
  port_number: PortNumber
  values: PortValues
  config: FullConfig<PortConfig>
  flags: PortFlag[]
  spec?: DevicePortSpec
}

/**
 * Uniquely define a port of any device.
 */
export type DevicePort = { deviceId: ID, portNumber: PortNumber }

/**
 * Types of rules.
 */
export type RuleType = 'FIXED_CALENDAR'

/**
 * Paramaters of avoidance of expensive hours -rule.
 */
export interface AvoidExpenseiveHoursRuleParams {
  area: AreaCode
  hours: number
  max_repeat: number
  name: PortValueName
  off_value: string
  on_value: string
  min_price?: number
}

/**
 * A boolean for every day
 */
export type WeekdaysToggles = [boolean, boolean, boolean, boolean, boolean, boolean, boolean]

/**
 * A time period with flags defining weekdays when it applies.
 */
export type CalendarTimeRange = {
  start: TimeHHMM
  stop: TimeHHMM
  weekdays: WeekdaysToggles
}

/**
 * Parameters for fixed calendar -rule.
 */
export interface FixedCalendarRuleParams {
  name: PortValueName
  off_value: string
  on_value: string
  intervals: CalendarTimeRange[]
  visuals: CalendarTimeRange[]
}

/**
 * Adjustable part of a rule.
 */
export type RuleParams = AvoidExpenseiveHoursRuleParams | FixedCalendarRuleParams

/**
 * A rule description.
 */
export interface Rule {
  id?: ID
  port_number?: PortNumber
  type: RuleType
  priority: number
  params: RuleParams
}

/**
 * Different kinds of activty log entries.
 */
export type ActivityType = 'LOGIN' |
  'LOGOUT' |
  'SYSBOOT' |
  'VISIT' |
  'CONFIG' |
  'MANUAL_ON' |
  'MANUAL_OFF' |
  'RELAY_ON' |
  'RELAY_OFF' |
  'RELAY_ON' |
  'RELAY_OFF' |
  'RULE_CHANGED' |
  'DEVBOOT'

/**
 * Activity log entry.
*/
export interface Activity {
  id: ID
  time: TimeString
  user_id: ID
  port_id: ID
  type: ActivityType
  text: string
  params: Record<string, unknown>
}

/**
 * Activity with pagination data.
*/
export interface ActivityWithPagination {
  current_page: number,
  total_pages: number,
  activities: Activity[]
}

/**
 * Scheduler data.
 */
export type ScheduleLine = [
  TimeString,
  {
    name: string,
    value: unknown
  }
]

export type OverrideSchedule = {
  port: PortNumber
  schedule: ScheduleLine[]
}

/**
 * Information about firmware.
 */
export interface Firmware {
  version: string
  hash: string
  order: number
  available: boolean
}

/**
 * Optimization or usage method.
 */
export type Strategy = 'MANUAL' | 'CALENDAR' | 'AUTO' | 'PV_ONLY' | 'AVOID_EXPENSIVE' | 'BATTERY_OPTIMIZING' | 'BATTERY_MANUAL'
export const strategies: Strategy[] = [
  'BATTERY_OPTIMIZING',
  'MANUAL',
  'CALENDAR',
  'AUTO',
  'PV_ONLY',
  'AVOID_EXPENSIVE',
]
export const strategyIcons: (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
  muiName: string;
})[] = [
  ChargingStation,
  PanTool,
  CalendarMonth,
  WbSunny,
  HdrAuto,
  Euro,
]

/**
 * Strategies offered for a battery (plain or reserve-market). Kept apart from
 * `strategies` because the two lists share no members beyond
 * `BATTERY_OPTIMIZING` and the icon arrays are index-paired with the items.
 *
 * `BATTERY_MANUAL` is maintenance mode: the firmware's battery optimizer
 * (port 0x220) is switched off and the battery takes direct power commands.
 */
export const batteryStrategies: Strategy[] = [
  'BATTERY_OPTIMIZING',
  'BATTERY_MANUAL',
]
export const batteryStrategyIcons: (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
  muiName: string;
})[] = [
  ChargingStation,
  Handyman,
]

/**
 * Additional state info passed when navigating.
 */
export type NavState = {
  prev: string
} | {
  disable: {
    port: ID
  }
}

export function isNavState(obj: unknown): obj is NavState {
  if (obj !== null && typeof obj === 'object') {
    const o = obj as { prev: unknown, disable: unknown}
    if ('prev' in o && typeof o.prev === 'string') return true
    if ('disable' in o && typeof o.disable === 'object' && o.disable !== null && 'port' in o.disable) return true
  }
  return false
}

/**
 * Global system setup.
 */
export interface SystemSetup {
  disable_firmware_updates?: boolean
}

/**
 * A system announcement.
 */
export interface Announcement {
  id?: ID
  text: string
  url?: string
  language: Language
  expires: TimeString
  time: TimeString
}
